import {
  U21Spacer,
  U21Subsection,
  U21SubsectionProps,
  U21Chip,
  U21TitleCountLabel,
  U21Typography,
} from 'app/shared/u21-ui/components';
import React from 'react';
import { useSelector } from 'react-redux';
import {
  selectTransactionFingerprintSettings,
  selectActionEventFingerprintSettings,
} from 'app/modules/dataSettings/selectors';
import { mapFieldsForSidebarSection } from 'app/modules/sidebar/components/utils/SidebarSummary';
import { FullTxnResponse } from 'app/modules/transactions/types';
import {
  CachedActionEventResponse,
  ShortActionEventResponse,
} from 'app/modules/actionEvents/responses';
import {
  useFingerprintCount,
  useFingerprintNewlySeen,
  useHasFingerprintData,
} from 'app/modules/deviceIntelligence/hooks/fingerprint';

type DeviceIntelligenceSidebarSectionProps = {
  event: 'txn' | 'action';
  details?:
    | FullTxnResponse
    | CachedActionEventResponse
    | ShortActionEventResponse;
  showEmptyFields: boolean;
};

export const DeviceIntelligenceSidebarSection = ({
  event,
  details,
  showEmptyFields,
}: DeviceIntelligenceSidebarSectionProps) => {
  const fingerprintSettings = useSelector(
    event === 'txn'
      ? selectTransactionFingerprintSettings
      : selectActionEventFingerprintSettings,
  );

  const trueCount = useFingerprintCount(event, details);
  const isNewlySeen = useFingerprintNewlySeen(event, details);

  const hasFingerprintData = useHasFingerprintData(event, details);

  return (
    <U21Spacer spacing={0.5} marginEnd marginStart>
      <WrappedSubsection
        collapsible
        collapsed
        title={
          <U21TitleCountLabel
            count={hasFingerprintData && trueCount > 0 ? trueCount : null}
            label=""
            color={trueCount > 0 ? 'error' : 'default'}
          >
            Device Signals
          </U21TitleCountLabel>
        }
        action={
          isNewlySeen ? (
            <U21Chip color="warning">Newly seen</U21Chip>
          ) : undefined
        }
      >
        {!hasFingerprintData ? (
          <U21Typography variant="caption" color="text.secondary">
            No device signals found
          </U21Typography>
        ) : (
          details &&
          fingerprintSettings.map((f) =>
            mapFieldsForSidebarSection(f, details, showEmptyFields),
          )
        )}
      </WrappedSubsection>
    </U21Spacer>
  );
};

const WrappedSubsection = ({ children, ...restProps }: U21SubsectionProps) => (
  <U21Subsection {...restProps} shadedHeader>
    <U21Spacer>{children}</U21Spacer>
  </U21Subsection>
);
